import { enqueueSnackbar } from "notistack";
import { useHistory } from "react-router-dom";
import { useState, useEffect } from "react";
import { useRecoilValue } from "recoil";
import { userState } from "../../../../../../recoil/auth";
import { CurrentReservation, PubTournament } from "../../../../../../api/types";
import { cancelCompanyReservation } from "../../../../../../api/company-reservation";

export const useRabbitFestivalReservation = (
  eventId: number,
  item?: PubTournament
) => {
  const history = useHistory();
  const user = useRecoilValue(userState);
  const [isCurrentReservation, setIsCurrentReservation] = useState(false);
  const [isReservationCancel, setIsReservationCancel] = useState(false);
  const [isReservationPopup, setIsReservationPopup] = useState(false);
  const [isReservation, setIsReservation] = useState<boolean>(false); // 해당 토너먼트 예약 여부
  const [isSpecial, setIsSpecial] = useState<boolean>(false); // 특별초대권 예약 여부
  const [userReservation, setUserReservation] =
    useState<CurrentReservation | null>(null); // 해당 토너먼트 예약 dto

  useEffect(() => {
    // 토너먼트 예약자 현황에서 현재 유저 정보 찾기
    if (item?.current && user) {
      const userReservation = item?.current.find(
        (current) => current.userId === user.id
      );
      if (userReservation) {
        setIsReservation(true);
        setIsSpecial(userReservation.route === "SPECIAL");
        setUserReservation(userReservation);
      } else {
        setIsReservation(false);
        setIsSpecial(false);
        setUserReservation(null);
      }
    }
  }, [item]);

  // 예약 가능 조건
  const canReservation = (): boolean => {
    if (!item?.reservation) {
      return false;
    }

    // 현재 레벨 존재할 경우
    const currentLevel = item?.timeInfo?.currentLevel.level;
    if (currentLevel) {
      if (item?.registerDeadlineLevel >= currentLevel) {
        return true;
      }
    } else {
      // break일 경우
      const level = item?.timeInfo?.nextLevel?.level - 1;
      if (item?.registerDeadlineLevel >= level) {
        return true;
      }
    }

    return false;
  };

  // 예약 마감 여부
  const reservationEnd = () => {
    if (!item?.reservation) return false;

    // 인원 마감
    if (
      item.reservation.limitEntry &&
      +item.reservationCount === +item.reservation.limitEntry
    )
      return true;

    // 수동 마감
    if (item.reservation.isEnd) return true;
    return false;
  };

  // 하단 버튼 텍스트
  const getReservationButtonText = (): string => {
    // 예약이 있는 경우
    if (item?.reservation) {
      if (item?.isEnd) return "토너먼트 진행결과";
      if (isReservation) {
        return "예약취소";
      }
      if (reservationEnd()) {
        return "예약 마감";
      }
      if (!canReservation()) {
        return "레지마감";
      }
      return `예약하기`;
    }
    // 예약이 없고 토너먼트가 시작 되었을 떄
    if (item?.timeInfo?.elapsedTimeMs && item?.timeInfo?.elapsedTimeMs > 0) {
      return `토너먼트 진행 현황`;
    }
    if (item?.isEnd) return "토너먼트 진행결과";
    return "시작 준비 중";
  };

  // 하단 좌측 버튼 클릭 이벤트
  const handleClickStateButton = () => {
    // 예약 가능하고 레지 마감 전
    if (
      item?.reservation &&
      !item?.isEnd &&
      item?.timeInfo?.elapsedTimeMs < 0
    ) {
      setIsCurrentReservation(true);
      return;
    }
    // 토너먼트 진행현황 페이지
    history.push(`/tournament/${item?.id}?event=${eventId}`);
  };

  // 하단 버튼 클릭 이벤트
  const handleClickButton = () => {
    // 토너먼트 종료 시 토너먼트 진행현황 페이지 (예약 유무 상관 X)
    if (item?.isEnd) {
      history.push(`/tournament/${item?.id}?event=${eventId}`);
    }
    // 예약이 없을 경우 토너먼트 진행현황 페이지
    if (!item?.reservation) {
      history.push(`/tournament/${item?.id}?event=${eventId}`);
      return;
    }
    // 유저가 예약했을 경우 -> 예약 취소 로직
    if (isReservation && userReservation) {
      // 특별초대권은 취소 불가
      if (isSpecial) {
        enqueueSnackbar(
          "특별초대권으로 예약된 토너먼트는 취소할 수 없습니다. ",
          { variant: "error" }
        );
        return;
      }
      // 예약취소
      setIsReservationCancel(true);
      return;
    }

    if (canReservation()) {
      if (!user) {
        enqueueSnackbar("로그인 후 예약 가능합니다. ", { variant: "error" });
        return;
      }
      setIsReservationPopup(true);
    }
  };

  // 래빗 페스티벌 3일 전 취소 불가
  const handleCanRunnerReservationCancel = () => {
    if (!userReservation || !item) return;
    const startDate = new Date(item.startDate);
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const startDateOnly = new Date(startDate);
    startDateOnly.setHours(0, 0, 0, 0);

    const dDayRemaining = Math.ceil(
      (startDateOnly.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (dDayRemaining <= 3) {
      enqueueSnackbar("대회 시작 3일 전에는 예약을 취소할 수 없습니다. ", {
        variant: "error",
      });
      setIsReservationCancel(false);
      return false;
    }
    return true;
  };

  // 예약 취소 로직
  const handleCancel = async () => {
    if (!userReservation) return;
    try {
      if (!handleCanRunnerReservationCancel()) return;
      await cancelCompanyReservation(userReservation.id);
      enqueueSnackbar("해당 토너먼트 예약 취소되었습니다. ", {
        variant: "success",
      });
      setIsReservationCancel(false);
    } catch (e: any) {
      enqueueSnackbar(e.message, {
        variant: "error",
      });
      setIsReservationCancel(false);
      console.error(e);
    }
  };

  return {
    // 텍스트
    getReservationButtonText,
    // 클릭 이벤트
    handleClickStateButton,
    handleClickButton,
    // 예약 가능 여부
    canReservation,
    // 유저 예약 여부
    isReservation,
    // 예약 취소 팝업
    isReservationCancel,
    setIsReservationCancel,
    // 예약 팝업
    isReservationPopup,
    setIsReservationPopup,
    // 예약자 목록 팝업
    isCurrentReservation,
    setIsCurrentReservation,
    // 예약 취소
    handleCancel,
    // 예약 마감 여부
    reservationEnd,
  };
};
